import React from "react";
const defaultConfig = {
    timeMs: 10000,
    expDecay: 1,
    startPollingImmediately: true,
    onlyUpdateOnChange: true,
};
/**
 * Polls a function that returns a promise every given milliseconds.
 * **Note:** this function will stop polling if the promise the poll function returns rejects.
 *
 * @param {()=>Promise} poll - function to poll. Must return a promise
 * @param {Config} config - config for this hook
 * @returns {any} the result of the promise from the poll function
 */
const usePoll = (poll, config = {}) => {
    const safeConfig = React.useMemo(() => (Object.assign(Object.assign({}, defaultConfig), config)), [config]);
    const [lastResult, setLastResult] = React.useState(null);
    const [pollCount, setPollCount] = React.useState(0);
    const [isPolling, setIsPolling] = React.useState(safeConfig.startPollingImmediately);
    const startPoll = React.useCallback(() => setIsPolling(true), []);
    const stopPoll = React.useCallback(() => setIsPolling(false), []);
    const pollRef = React.useRef(poll);
    React.useEffect(() => {
        pollRef.current = poll;
    }, [poll]);
    React.useEffect(() => {
        // @ts-expect-error - TS7034 - Variable 'timeout' implicitly has type 'any' in some locations where its type cannot be determined.
        let timeout;
        if (isPolling) {
            timeout = setTimeout(() => {
                if (isPolling) {
                    pollRef
                        .current({ isPolling, startPoll, stopPoll })
                        .then((results) => {
                        if (!safeConfig.onlyUpdateOnChange || results !== lastResult) {
                            setLastResult(results);
                        }
                        setPollCount(pollCount + 1);
                    });
                }
            }, safeConfig.timeMs * Math.pow(Math.max(safeConfig.expDecay, 1), pollCount));
        }
        return () => {
            // @ts-expect-error - TS7005 - Variable 'timeout' implicitly has an 'any' type.
            if (timeout != null) {
                // @ts-expect-error - TS7005 - Variable 'timeout' implicitly has an 'any' type.
                clearTimeout(timeout);
            }
        };
    }, [
        isPolling,
        lastResult,
        pollCount,
        safeConfig.expDecay,
        safeConfig.onlyUpdateOnChange,
        safeConfig.timeMs,
        startPoll,
        stopPoll,
    ]);
    return React.useMemo(() => {
        return [lastResult, { isPolling, startPoll, stopPoll }];
    }, [isPolling, lastResult, startPoll, stopPoll]);
};
export default usePoll;
